import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Clock, Users, Star } from 'lucide-react';
import { useSurveyTemplate } from '@/hooks/useSurveyTemplates';
import { SurveyTemplateSEO } from '@/components/SEO';

interface SurveyTemplateViewerProps {
  templateId: string;
  onBack: () => void;
  onUseTemplate?: (templateId: string) => void;
}

export const SurveyTemplateViewer = ({ templateId, onBack, onUseTemplate }: SurveyTemplateViewerProps) => {
  const { data: template, isLoading, error } = useSurveyTemplate(templateId);

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to templates
          </Button>
        </div>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-3/4 sm:w-1/2" />
            <Skeleton className="h-4 w-full sm:w-3/4" />
            <div className="flex gap-3 sm:gap-4 mt-4 flex-wrap">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-24" />
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-3 sm:space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-4 sm:pt-6">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
        </div>
        <div className="text-center py-8">
          <p className="text-muted-foreground">Failed to load template details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* SEO Meta Tags */}
      <SurveyTemplateSEO template={template} />
      <div className="flex items-center gap-2 sm:gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Templates
        </Button>
      </div>

      {/* Template Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1">
              <CardTitle className="flex flex-wrap items-center gap-2 text-lg sm:text-2xl">
                {template.name}
                {template.is_featured && (
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                )}
              </CardTitle>
              <CardDescription className="mt-2 text-sm sm:text-base">
                {template.description}
              </CardDescription>
            </div>
            {onUseTemplate && (
              <Button onClick={() => onUseTemplate(template.id)} className="w-full sm:w-auto bg-gradient-primary text-white">
                Use this template
              </Button>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-muted-foreground">
            {template.estimated_duration_minutes && (
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Estimated {template.estimated_duration_minutes} minutes
              </div>
            )}
            {template.target_audience && (
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {template.target_audience}
              </div>
            )}
            <Badge variant="secondary" className="capitalize">
              {template.category}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Questions */}
      <div className="space-y-3 sm:space-y-4">
        <h3 className="text-base sm:text-lg font-semibold">
          Questions ({template.questions.length})
        </h3>
        
        {template.questions.map((question, index) => (
          <Card key={question.id} className="border-l-0 sm:border-l-4 sm:border-l-primary/20">
            <CardContent className="pt-4 sm:pt-6">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                        Question {index + 1}
                      </span>
                      {question.is_required && (
                        <Badge variant="secondary">Required</Badge>
                      )}
                      {question.is_reverse_scored && (
                        <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                          Reverse Scored
                        </Badge>
                      )}
                      <Badge variant="outline">
                        {question.question_type.name}
                      </Badge>
                    </div>
                    <p className="text-sm sm:text-base font-medium mb-2">
                      {question.question_text}
                    </p>
                  </div>
                </div>

                {/* Focus Areas */}
                {question.focus_areas.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {question.focus_areas.map((area) => (
                      <Badge key={area.id} variant="secondary">
                        {area.name}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Options for multiple choice questions */}
                {question.options && Array.isArray(question.options) && question.options.length > 0 && (
                  <div className="ml-0 sm:ml-4 space-y-1">
                    <p className="text-xs text-muted-foreground">Options:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {question.options.map((option: string, optIndex: number) => (
                        <li key={optIndex} className="flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full border-2 border-muted-foreground/30"></span>
                          {option}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};